import PropTypes from "prop-types";
import makeStyles from "@mui/styles/makeStyles";
import React from "react";
import { Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { activityTypes } from "../../../../constants";
import VideoPlayer from "../../../../components/VideoPlayer/VideoPlayer";
import AudioPlayer from "../../../../components/AudioPlayer/AudioPlayer";
import Modal from "../../../../components/Modal/index";
import GoogleDocsViewer from "../../../../components/GoogleDocsViewer/GoogleDocsViewer";
import PdfViewer from "../../../../components/PdfViewer/PdfViewer";

const iframeStyles = makeStyles(theme => ({
  container: {
    position: "relative",
    paddingTop: "56.25%",
    marginBottom: theme.spacing(7)
  },
  iframe: {
    position: "absolute",
    top: "0",
    left: "0",
    width: "100%",
    height: "100%"
  }
}));

const IFrameViewer = ({ iframeRef, contentLink, classes }) => {
  return (
    <div className={classes.container}>
      <iframe
        ref={iframeRef}
        allowFullScreen
        src={contentLink}
        frameBorder="0"
        title="iframe"
        className={classes.iframe}
      />
    </div>
  );
};

const isLocalFile = url => {
  return url.includes("localhost");
};

const ItemPreview = ({ previewUrl, mimeType, type }) => {
  const iframeClasses = iframeStyles();
  let iframeRef = React.useRef(null);

  if (type === activityTypes.video) {
    return (
      <VideoPlayer mimeType={mimeType} contentLink={previewUrl} disableMargin />
    );
  } else if (type === activityTypes.audio) {
    return <AudioPlayer src={previewUrl} />;
  } else if (type === activityTypes.pdf) {
    return <PdfViewer link={previewUrl} />;
  } else if (type === activityTypes.document && !isLocalFile(previewUrl)) {
    return <GoogleDocsViewer url={previewUrl} height="700px" />;
  } else if (type === activityTypes.page || type === activityTypes.link) {
    return (
      <IFrameViewer
        iframeRef={iframeRef}
        contentLink={previewUrl}
        classes={iframeClasses}
      />
    );
  } else if (type === activityTypes.image) {
    return <img width="100%" src={previewUrl} alt="Preview" />;
  } else if (type === activityTypes.scorm) {
    return (
      <iframe
        style={{ width: "100%", height: "100%" }}
        allowFullScreen
        src={previewUrl}
        frameBorder="0"
        title="Scorm Preview"
        data-qa="repo-item-scorm-preview-iframe"
      />
    );
  } else {
    // TODO: Add other preview types in future tickets
    return null;
  }
};

const ItemPreviewModal = ({
  itemName,
  itemType,
  itemMimeType,
  previewUrl,
  open,
  onClose
}) => {
  const { t } = useTranslation();

  const modalSize = itemType === activityTypes.scorm ? "lg" : "md";
  const fillHeight = itemType === activityTypes.scorm;

  return (
    <Modal
      showCloseIcon
      dialogTitle={
        <Typography variant="h4" color="textPrimary">
          {t("manageRepository.previewModalTitle", { name: itemName })}
        </Typography>
      }
      open={open}
      onClose={onClose}
      dialogContent={
        <ItemPreview
          previewUrl={previewUrl}
          mimeType={itemMimeType}
          type={itemType}
        />
      }
      maxWidth={modalSize}
      fullWidth
      fillHeight={fillHeight}
      scrollable
      data-qa="repository-item-preview-modal"
    />
  );
};

ItemPreviewModal.propTypes = {
  /**
   * True if the item preview modal is open, false otherwise
   */
  open: PropTypes.bool,
  /**
   * Called when the item preview modal is closed
   */
  onClose: PropTypes.func,
  itemName: PropTypes.string,
  itemType: PropTypes.oneOf(Object.values(activityTypes)),
  itemMimeType: PropTypes.string,
  previewUrl: PropTypes.string
};

export default ItemPreviewModal;
